import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { discoverRelayList } from '@/lib/outbox';
import { BLOG_OWNER_PUBKEY } from '@/lib/blogConfig';

export interface ArticleInteractions {
  comments: NostrEvent[];
  reactions: NostrEvent[];
  zaps: NostrEvent[];
  commentCount: number;
  reactionCount: number;
  zapCount: number;
  zapTotal: number;
}

/** Parse sats amount from a zap receipt (kind 9735). */
function zapAmountSats(zap: NostrEvent): number {
  // Prefer the amount from the embedded zap request description.
  try {
    const descTag = zap.tags.find(([n]) => n === 'description')?.[1];
    if (descTag) {
      const req = JSON.parse(descTag) as NostrEvent;
      const amt = req.tags.find(([n]) => n === 'amount')?.[1];
      if (amt) return Math.floor(parseInt(amt, 10) / 1000);
    }
  } catch {
    // ignore
  }
  return 0;
}

/**
 * Fetch all engagement (comments, reactions, zaps) for a given article,
 * following the outbox model: read from the owner's read relays (where events
 * "about" them are delivered per NIP-65).
 */
export function useArticleInteractions(coordinate: string | undefined) {
  const { nostr } = useNostr();

  return useQuery<ArticleInteractions>({
    queryKey: ['article-interactions', coordinate ?? ''],
    queryFn: async (c) => {
      const empty: ArticleInteractions = {
        comments: [],
        reactions: [],
        zaps: [],
        commentCount: 0,
        reactionCount: 0,
        zapCount: 0,
        zapTotal: 0,
      };
      if (!coordinate) return empty;

      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(8000)]);

      // Read from the owner's read relays (inbox) — that's where interactions
      // about their content are delivered under the outbox model. Also include
      // their write relays as a supplement (many clients post replies there).
      const relayList = await discoverRelayList(nostr, BLOG_OWNER_PUBKEY, { signal });
      const relays = [...new Set([...relayList.read, ...relayList.write])];
      const group = nostr.group(relays);

      // Query comments (NIP-22 kind 1111), reactions (kind 7), and zaps
      // (kind 9735) in a single round-trip, filtered on the article's `a` coord.
      const events = await group.query(
        [
          { kinds: [1111], '#A': [coordinate], limit: 500 },
          { kinds: [1111], '#a': [coordinate], limit: 500 },
          { kinds: [7], '#a': [coordinate], limit: 500 },
          { kinds: [9735], '#a': [coordinate], limit: 500 },
        ],
        { signal },
      );

      const commentsMap = new Map<string, NostrEvent>();
      const reactionsMap = new Map<string, NostrEvent>();
      const zapsMap = new Map<string, NostrEvent>();

      for (const ev of events) {
        if (ev.kind === 1111) commentsMap.set(ev.id, ev);
        else if (ev.kind === 7) reactionsMap.set(ev.id, ev);
        else if (ev.kind === 9735) zapsMap.set(ev.id, ev);
      }

      const comments = [...commentsMap.values()].sort(
        (a, b) => a.created_at - b.created_at,
      );
      const reactions = [...reactionsMap.values()];
      const zaps = [...zapsMap.values()];
      const zapTotal = zaps.reduce((sum, z) => sum + zapAmountSats(z), 0);

      return {
        comments,
        reactions,
        zaps,
        commentCount: comments.length,
        reactionCount: reactions.length,
        zapCount: zaps.length,
        zapTotal,
      };
    },
    enabled: !!coordinate,
    staleTime: 30 * 1000,
  });
}
